import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MessageCircle, X, Send, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConciergeChat = () => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (name.trim().length < 1 || !/^\S+@\S+\.\S+$/.test(email) || message.trim().length < 2) {
      toast.error("Please add your name, email, and a short message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/chat`, { name, email, message });
      setSent(true);
      setMessage("");
      toast.success("Message received — our concierge will reply shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        type="button"
        aria-label="Chat with concierge"
        data-testid="concierge-launcher"
        onClick={() => setOpen((v) => !v)}
        className="fixed z-50 bottom-5 right-5 md:bottom-7 md:right-7 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-[0_16px_40px_-12px_rgba(63,109,138,0.55)] flex items-center justify-center hover:scale-105 transition-transform"
      >
        {open ? <X size={20} /> : <MessageCircle size={22} />}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="concierge-panel"
          className="fixed z-50 bottom-24 right-4 md:right-7 w-[calc(100vw-2rem)] md:w-[380px] rounded-2xl border border-border bg-background shadow-2xl overflow-hidden fade-up"
        >
          <div className="p-5 bg-primary text-primary-foreground">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-80">
              <ShieldCheck size={13} /> Care Anywhere Concierge
            </div>
            <div className="mt-1 font-serif-display text-xl leading-tight">
              We usually reply within an hour.
            </div>
          </div>
          {sent ? (
            <div data-testid="concierge-sent" className="p-6 text-sm text-muted-foreground">
              <div className="font-medium text-foreground mb-2">Message received</div>
              A member of our concierge team will follow up shortly at{" "}
              <span className="text-primary">{email}</span>.
              <div className="mt-5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setSent(false); }}
                  className="rounded-full"
                >
                  Send another
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="concierge-form" className="p-5 space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Name</Label>
                <Input
                  data-testid="concierge-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="mt-1.5 h-10 rounded-lg"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
                <Input
                  data-testid="concierge-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@family.com"
                  className="mt-1.5 h-10 rounded-lg"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Message</Label>
                <Textarea
                  data-testid="concierge-message"
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="How can we help?"
                  className="mt-1.5 rounded-lg resize-none"
                />
              </div>
              <Button
                type="submit"
                data-testid="concierge-send"
                disabled={sending}
                className="w-full rounded-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {sending ? "Sending…" : (
                  <span className="flex items-center gap-2 justify-center">Send <Send size={14} /></span>
                )}
              </Button>
            </form>
          )}
        </div>
      )}
    </>
  );
};

export default ConciergeChat;
